import * as React from 'react';
import PulseLoader from 'react-spinners/PulseLoader';
import { Flex, Box } from 'theme-ui';
import { Formik, FormikConfig } from 'formik';
import * as Yup from 'yup';
import { getAuth, signInWithEmailLink, isSignInWithEmailLink } from 'firebase/auth';

import { BackToLogin, EmailField, FormHeader, FormSubHeader, ButtonLarge } from '@mdlinx/component-library';

import { useLoginWithIdTokenMutation } from 'generated/client';
import { useFirebaseApp } from 'lib/firebaseApp';
import { FormContainer } from 'app/components/layout/form-container';

const { useEffect } = React;

const GENERIC_ERROR_MESSAGE = 'We were unable to log you in. Please try another login method';
const EMAIL_FOR_SIGN_IN = 'emailForSignIn';

export const SignInWithOobCode: React.FC<{
  parentUrl: string;
  onSuccess: () => void;
  onClickBack: () => void;
}> = ({ parentUrl, onSuccess, onClickBack }) => {
  const [loading, setLoadingState] = React.useState(true);
  const firebaseApp = useFirebaseApp();
  const [loginWithIdToken] = useLoginWithIdTokenMutation();
  const [errorMessage, setErrorMessage] = React.useState('');
  const submitStarted = React.useRef(false);

  const handleLogin = (email: string, parentUrl: string) => {
    signInWithEmailLink(getAuth(firebaseApp), email, parentUrl)
      .then(result => result?.user?.getIdToken())
      .then(idToken => {
        window.localStorage.removeItem(EMAIL_FOR_SIGN_IN);
        if (!idToken) {
          console.error('Failed to get ID token for logged in user');
          setErrorMessage(GENERIC_ERROR_MESSAGE);
          setLoadingState(false);
          return;
        }

        return loginWithIdToken({ variables: { idToken } });
      })
      .then(() => {
        onSuccess();
      })
      .catch(error => {
        setLoadingState(false);
        console.error('Error logging in with email link', error);
        setErrorMessage(GENERIC_ERROR_MESSAGE);
      });
  };

  useEffect(() => {
    if (isSignInWithEmailLink(getAuth(firebaseApp), parentUrl)) {
      const email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN);
      if (!email) {
        console.warn('Email address is required to sign in with a login link but none was found in local storage');
        setLoadingState(false);
        return;
      }

      handleLogin(email, parentUrl);
    } else {
      setLoadingState(false);
      console.error('The auto-login URL was malformed. Unable to log the user in.');
      setErrorMessage(GENERIC_ERROR_MESSAGE);
    }
  }, []);

  const onEmailFormSubmit: FormikConfig<{ email: string }>['onSubmit'] = ({ email }) => {
    setLoadingState(true);
    handleLogin(email, parentUrl);
  };

  if (loading) {
    return (
      <FormContainer>
        <Box sx={{ margin: '0 auto' }}>
          <PulseLoader size={8} color="rgba(255, 255, 255, 0.6)" loading={loading} />
        </Box>
      </FormContainer>
    );
  }

  if (errorMessage) {
    return (
      <FormContainer>
        <span style={{ color: 'red' }}>{errorMessage}</span>
        <BackToLogin onClick={onClickBack} />
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Flex sx={{ flexDirection: 'column' }}>
        <FormHeader>Auto-login link</FormHeader>
        <Box mt="12px">
          <FormSubHeader>
            It looks like you opened your auto-login link from a different web browser. Please enter your email address
            below so that we can confirm that it's you.
          </FormSubHeader>
        </Box>
        <Box mt="12px">
          <FormSubHeader>
            Alternatively, if your email app opened the link in a mini-browser, please try re-opening the link in your
            primary browser.
          </FormSubHeader>
        </Box>
        <Box mt="20px" />
        <Box width="100%" mb={['50px']}>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={Yup.object({
              email: Yup.string()
                .email('Invalid email addresss')
                .required('Required'),
            })}
            onSubmit={onEmailFormSubmit}
          >
            {formik => {
              const handleSubmitButtonClick = (event: React.SyntheticEvent) => {
                event.preventDefault();
                event.stopPropagation();
                submitStarted.current = true;
                formik.handleSubmit();
              };

              return (
                <>
                  <EmailField
                    submitStarted={submitStarted}
                    onSubmitClicked={() => formik.handleSubmit()}
                    isSubmitButtonDisabled={!(formik.dirty && formik.isValid)}
                  />

                  <Box mt="10px" />
                  <ButtonLarge
                    type="submit"
                    disabled={!(formik.dirty && formik.isValid)}
                    isLoading={formik.isSubmitting}
                    onClick={handleSubmitButtonClick}
                  >
                    Continue
                  </ButtonLarge>
                </>
              );
            }}
          </Formik>
        </Box>
      </Flex>
    </FormContainer>
  );
};
